/**
 * BPMN DSL Parser
 *
 * Task 1: TypeScript interfaces, BPMN_TYPE_MAP, ID_PREFIX, and getBpmnId helper.
 * Later tasks will add the full parser and XML generator functions.
 */

// ---------------------------------------------------------------------------
// Exported interfaces
// ---------------------------------------------------------------------------

export interface BoundaryEventDef {
  localName: string;
  kind: BpmnKind | string;
  label?: string;
}

export interface ElementDef {
  varName: string;
  kind: BpmnKind | string;
  label?: string;
  boundaryEvents: BoundaryEventDef[];
  subprocessChildren?: SubprocessContent;
}

export interface SubprocessContent {
  elements: ElementDef[];
  connections: ConnectionDef[];
}

export interface LaneDef {
  name: string;
  elements: ElementDef[];
  systemCode?: string; // <<SYSTEM_CODE>> annotation
}

export interface ConnectionDef {
  from: string;
  to: string;
  label?: string;
}

export interface AssociationDef {
  from: string;
  to: string;
}

export interface DiagramAst {
  name: string;
  lanes: LaneDef[];
  globalElements: ElementDef[];
  connections: ConnectionDef[];
  associations: AssociationDef[];
  labels: Record<string, string>;
}

// ---------------------------------------------------------------------------
// DSL Element Map — used for round-trip / reverse sync
// ---------------------------------------------------------------------------

export interface DslElementEntry {
  kind: BpmnKind | string;
  varName: string;
  label?: string;
  laneName?: string; // undefined for globalElements
}

export type DslElementMap = Map<string, DslElementEntry>; // bpmnId → entry

/**
 * Builds a map from BPMN element IDs (as generated by getBpmnId) back to
 * their DSL representation. Call this right after parseBpmnDsl.
 */
export const buildElementMap = (ast: DiagramAst): DslElementMap => {
  const map: DslElementMap = new Map();

  for (const lane of ast.lanes) {
    for (const el of lane.elements) {
      map.set(getBpmnId(el.varName, el.kind), {
        kind: el.kind, varName: el.varName, label: el.label, laneName: lane.name,
      });
      for (const be of el.boundaryEvents) {
        const beKey = `${el.varName}.${be.localName}`;
        map.set(getBpmnId(beKey, be.kind), {
          kind: be.kind, varName: beKey, label: be.label, laneName: lane.name,
        });
      }
    }
  }

  for (const el of ast.globalElements) {
    map.set(getBpmnId(el.varName, el.kind), {
      kind: el.kind, varName: el.varName, label: el.label,
    });
  }

  return map;
};

// ---------------------------------------------------------------------------
// Internal type definition interface
// ---------------------------------------------------------------------------

export interface BpmnTypeDef {
  tag: string;
  w: number;
  h: number;
  eventDef?: string;
  isNonInterrupting?: boolean;
}

// ---------------------------------------------------------------------------
// BPMN_TYPE_MAP
// ---------------------------------------------------------------------------

export const BPMN_TYPE_MAP = {
  // Start events
  'Start':                  { tag: 'bpmn:startEvent', w: 36, h: 36 },
  'Start.Message':          { tag: 'bpmn:startEvent', w: 36, h: 36, eventDef: 'messageEventDefinition' },
  'Start.Timer':            { tag: 'bpmn:startEvent', w: 36, h: 36, eventDef: 'timerEventDefinition' },
  'Start.Conditional':      { tag: 'bpmn:startEvent', w: 36, h: 36, eventDef: 'conditionalEventDefinition' },
  'Start.Signal':           { tag: 'bpmn:startEvent', w: 36, h: 36, eventDef: 'signalEventDefinition' },

  // Intermediate catch events
  'Event.Message':          { tag: 'bpmn:intermediateCatchEvent', w: 36, h: 36, eventDef: 'messageEventDefinition' },
  'Event.Timer':            { tag: 'bpmn:intermediateCatchEvent', w: 36, h: 36, eventDef: 'timerEventDefinition' },
  'Event.Conditional':      { tag: 'bpmn:intermediateCatchEvent', w: 36, h: 36, eventDef: 'conditionalEventDefinition' },
  'Event.Signal':           { tag: 'bpmn:intermediateCatchEvent', w: 36, h: 36, eventDef: 'signalEventDefinition' },
  'Event.Link':             { tag: 'bpmn:intermediateCatchEvent', w: 36, h: 36, eventDef: 'linkEventDefinition' },

  // Intermediate throw events
  'Throw.Message':          { tag: 'bpmn:intermediateThrowEvent', w: 36, h: 36, eventDef: 'messageEventDefinition' },
  'Throw.Signal':           { tag: 'bpmn:intermediateThrowEvent', w: 36, h: 36, eventDef: 'signalEventDefinition' },
  'Throw.Escalation':       { tag: 'bpmn:intermediateThrowEvent', w: 36, h: 36, eventDef: 'escalationEventDefinition' },
  'Throw.Link':             { tag: 'bpmn:intermediateThrowEvent', w: 36, h: 36, eventDef: 'linkEventDefinition' },

  // End events
  'End':                    { tag: 'bpmn:endEvent', w: 36, h: 36 },
  'End.Message':            { tag: 'bpmn:endEvent', w: 36, h: 36, eventDef: 'messageEventDefinition' },
  'End.Error':              { tag: 'bpmn:endEvent', w: 36, h: 36, eventDef: 'errorEventDefinition' },
  'End.Escalation':         { tag: 'bpmn:endEvent', w: 36, h: 36, eventDef: 'escalationEventDefinition' },
  'End.Signal':             { tag: 'bpmn:endEvent', w: 36, h: 36, eventDef: 'signalEventDefinition' },
  'End.Cancel':             { tag: 'bpmn:endEvent', w: 36, h: 36, eventDef: 'cancelEventDefinition' },
  'End.Terminate':          { tag: 'bpmn:endEvent', w: 36, h: 36, eventDef: 'terminateEventDefinition' },

  // Boundary events
  'Boundary.Timer':         { tag: 'bpmn:boundaryEvent', w: 36, h: 36, eventDef: 'timerEventDefinition' },
  'Boundary.Timer.NI':      { tag: 'bpmn:boundaryEvent', w: 36, h: 36, eventDef: 'timerEventDefinition', isNonInterrupting: true },
  'Boundary.Error':         { tag: 'bpmn:boundaryEvent', w: 36, h: 36, eventDef: 'errorEventDefinition' },
  'Boundary.Message':       { tag: 'bpmn:boundaryEvent', w: 36, h: 36, eventDef: 'messageEventDefinition' },
  'Boundary.Message.NI':    { tag: 'bpmn:boundaryEvent', w: 36, h: 36, eventDef: 'messageEventDefinition', isNonInterrupting: true },
  'Boundary.Escalation':    { tag: 'bpmn:boundaryEvent', w: 36, h: 36, eventDef: 'escalationEventDefinition' },
  'Boundary.Escalation.NI': { tag: 'bpmn:boundaryEvent', w: 36, h: 36, eventDef: 'escalationEventDefinition', isNonInterrupting: true },
  'Boundary.Signal':        { tag: 'bpmn:boundaryEvent', w: 36, h: 36, eventDef: 'signalEventDefinition' },
  'Boundary.Signal.NI':     { tag: 'bpmn:boundaryEvent', w: 36, h: 36, eventDef: 'signalEventDefinition', isNonInterrupting: true },
  'Boundary.Compensation':  { tag: 'bpmn:boundaryEvent', w: 36, h: 36, eventDef: 'compensateEventDefinition', isNonInterrupting: true },
  'Boundary.Cancel':        { tag: 'bpmn:boundaryEvent', w: 36, h: 36, eventDef: 'cancelEventDefinition' },

  // Tasks
  'Task':                   { tag: 'bpmn:task', w: 100, h: 80 },
  'Task.User':              { tag: 'bpmn:userTask', w: 100, h: 80 },
  'Task.Service':           { tag: 'bpmn:serviceTask', w: 100, h: 80 },
  'Task.Send':              { tag: 'bpmn:sendTask', w: 100, h: 80 },
  'Task.Receive':           { tag: 'bpmn:receiveTask', w: 100, h: 80 },
  'Task.Manual':            { tag: 'bpmn:manualTask', w: 100, h: 80 },
  'Task.Script':            { tag: 'bpmn:scriptTask', w: 100, h: 80 },
  'Task.Business':          { tag: 'bpmn:businessRuleTask', w: 100, h: 80 },

  // Subprocesses
  'Subprocess':             { tag: 'bpmn:subProcess', w: 100, h: 80 },
  'CallActivity':           { tag: 'bpmn:callActivity', w: 100, h: 80 },
  'Subprocess.Transaction': { tag: 'bpmn:transaction', w: 100, h: 80 },

  // Gateways
  'Gateway':                { tag: 'bpmn:exclusiveGateway', w: 50, h: 50 },
  'Gateway.Exclusive':      { tag: 'bpmn:exclusiveGateway', w: 50, h: 50 },
  'Gateway.Parallel':       { tag: 'bpmn:parallelGateway', w: 50, h: 50 },
  'Gateway.Inclusive':      { tag: 'bpmn:inclusiveGateway', w: 50, h: 50 },
  'Gateway.Event':          { tag: 'bpmn:eventBasedGateway', w: 50, h: 50 },

  // Artifacts & Data
  'Annotation':             { tag: 'bpmn:textAnnotation', w: 100, h: 40 },
  'Group':                  { tag: 'bpmn:group', w: 300, h: 200 },
  'Data.Object':            { tag: 'bpmn:dataObjectReference', w: 36, h: 50 },
  'Data.Store':             { tag: 'bpmn:dataStoreReference', w: 50, h: 50 },
} satisfies Record<string, BpmnTypeDef>;

export type BpmnKind = keyof typeof BPMN_TYPE_MAP;

// ---------------------------------------------------------------------------
// ID_PREFIX — internal, not exported
// ---------------------------------------------------------------------------

const ID_PREFIX: Record<string, string> = {
  'bpmn:startEvent':           'StartEvent',
  'bpmn:intermediateCatchEvent': 'CatchEvent',
  'bpmn:intermediateThrowEvent': 'ThrowEvent',
  'bpmn:endEvent':             'EndEvent',
  'bpmn:boundaryEvent':        'BoundaryEvent',
  'bpmn:task':                 'Task',
  'bpmn:userTask':             'UserTask',
  'bpmn:serviceTask':          'ServiceTask',
  'bpmn:sendTask':             'SendTask',
  'bpmn:receiveTask':          'ReceiveTask',
  'bpmn:manualTask':           'ManualTask',
  'bpmn:scriptTask':           'ScriptTask',
  'bpmn:businessRuleTask':     'BusinessRuleTask',
  'bpmn:subProcess':           'SubProcess',
  'bpmn:callActivity':         'CallActivity',
  'bpmn:transaction':          'Transaction',
  'bpmn:exclusiveGateway':     'ExclusiveGateway',
  'bpmn:parallelGateway':      'ParallelGateway',
  'bpmn:inclusiveGateway':     'InclusiveGateway',
  'bpmn:eventBasedGateway':    'EventBasedGateway',
  'bpmn:textAnnotation':       'TextAnnotation',
  'bpmn:group':                'Group',
  'bpmn:dataObjectReference':  'DataObjectRef',
  'bpmn:dataStoreReference':   'DataStoreRef',
};

// ---------------------------------------------------------------------------
// getBpmnId
// ---------------------------------------------------------------------------

export const getBpmnId = (varName: string, kind: BpmnKind | string): string => {
  const typeDef = (BPMN_TYPE_MAP as Record<string, BpmnTypeDef | undefined>)[kind];
  const prefix = typeDef ? (ID_PREFIX[typeDef.tag] ?? 'Element') : 'Element';
  return `${prefix}_${varName.replace(/\./g, '_')}`;
};

// ─── Tokenizer ────────────────────────────────────────────────────────────────

/** Strip comments and blank lines. Returns trimmed non-empty lines. */
export const cleanLines = (src: string): string[] =>
  src
    .split('\n')
    .map((l) => l.replace(/\/\/.*$/, '').trim())
    .filter(Boolean);

/**
 * Given lines and the index of the line containing `{`,
 * returns the inner lines (between { and matching }) and the index
 * of the line AFTER the closing `}`.
 *
 * The opening `{` may be on the same line as the block keyword.
 */
export const extractBlock = (
  lines: string[],
  startIdx: number
): { inner: string[]; nextIdx: number } => {
  const inner: string[] = [];
  let depth = 0;
  let i = startIdx;

  for (; i < lines.length; i++) {
    const line = lines[i];
    const opens = (line.match(/\{/g) || []).length;
    const closes = (line.match(/\}/g) || []).length;

    if (depth === 0 && opens > 0) {
      // Opening line — strip everything up to and including first `{`
      depth += opens - closes;
      const afterOpen = line.slice(line.indexOf('{') + 1);
      if (depth <= 0) {
        // Block opened and closed on the same line: extract only content between { and last }
        const closeIdx = afterOpen.lastIndexOf('}');
        const between = closeIdx !== -1 ? afterOpen.slice(0, closeIdx).trim() : afterOpen.trim();
        if (between) inner.push(between);
        i++;
        break;
      }
      const afterBrace = afterOpen.trim();
      if (afterBrace) inner.push(afterBrace);
      continue;
    }

    depth += opens - closes;
    if (depth <= 0) {
      // Closing `}` line — stop
      i++;
      break;
    }
    inner.push(line.trim());
  }

  return { inner, nextIdx: i };
};

// ─── Element Declaration Parser ───────────────────────────────────────────────

interface RawElementLine {
  varName: string;
  kind: BpmnKind | string;
  label?: string;
}

/**
 * Attempts to parse a line as an element declaration:
 *   varName = TypeExpr
 *   varName = TypeExpr "Label"
 *   host.local = Boundary.Type "label"
 *
 * Returns null if the line is not an element declaration.
 */
export const parseElementLine = (line: string): RawElementLine | null => {
  // Must contain `=` and not be a connection or diagram/lane keyword
  if (!line.includes('=')) return null;
  if (line.includes('->') || line.includes('-->')) return null;
  if (/^(diagram|lane|labels|flow)\s/.test(line)) return null;

  const eqIdx = line.indexOf('=');
  const varName = line.slice(0, eqIdx).trim();
  const rest = line.slice(eqIdx + 1).trim();

  // varName: snake_case optionally with a single dot (boundary event)
  if (!/^[a-zA-Z_][a-zA-Z0-9_]*(\.[a-zA-Z_][a-zA-Z0-9_]*)?$/.test(varName)) return null;

  // rest: TypeExpr optionally followed by "label"
  const match = rest.match(/^([A-Z][A-Za-z0-9.]*?)(?:\s+"([^"]*)")?$/);
  if (!match) return null;

  return {
    varName,
    kind: match[1],
    label: match[2],
  };
};

// ─── Connection Parser ────────────────────────────────────────────────────────

interface ParsedConnections {
  connections: ConnectionDef[];
  associations: AssociationDef[];
}

/**
 * Parses a single line for sequence flows (->) or associations (-->).
 * Supports chained flows: a -> b -> c
 * Label comes after the last arrow pair, separated by `: `:
 *   a -> b: Label   (label applies to the a→b flow)
 * For chains: a -> b -> c  (no labels on chained segments)
 */
export const parseConnections = (line: string): ParsedConnections => {
  const result: ParsedConnections = { connections: [], associations: [] };

  // Association (--> takes priority over ->)
  if (line.includes('-->')) {
    const parts = line.split('-->').map((s) => s.trim());
    if (parts.length === 2 && parts[0] && parts[1]) {
      result.associations.push({ from: parts[0], to: parts[1] });
    }
    return result;
  }

  // Sequence flow (potentially chained)
  if (!line.includes('->')) return result;

  // Split on '->' to get segments, then handle trailing ': label' on the last target
  const segments = line.split('->').map((s) => s.trim());
  if (segments.length < 2) return result;

  for (let i = 0; i < segments.length - 1; i++) {
    const from = segments[i];
    let to = segments[i + 1];
    let label: string | undefined;

    // Label is only on the last pair, after ': '
    if (i === segments.length - 2) {
      const colonIdx = to.indexOf(':');
      if (colonIdx !== -1) {
        label = to.slice(colonIdx + 1).trim() || undefined;
        to = to.slice(0, colonIdx).trim();
      }
    }

    if (from && to) {
      result.connections.push({ from, to, label });
    }
  }

  return result;
};

// ─── Full Diagram Parser ──────────────────────────────────────────────────────

/**
 * Parses a list of inner lines (from a lane or subprocess block) into elements and flows.
 * Boundary event lines (varName.prop = Boundary.*) are attached to their host element.
 */
const parseScopeLines = (
  lines: string[]
): { elements: ElementDef[]; connections: ConnectionDef[]; associations: AssociationDef[] } => {
  const elements: ElementDef[] = [];
  const connections: ConnectionDef[] = [];
  const associations: AssociationDef[] = [];
  // Boundary events whose host hasn't been seen yet
  const pendingBoundary: Array<{ hostVar: string; localName: string; kind: BpmnKind | string; label?: string }> = [];

  for (const line of lines) {
    // Try connection/association first
    const conn = parseConnections(line);
    if (conn.connections.length || conn.associations.length) {
      connections.push(...conn.connections);
      associations.push(...conn.associations);
      continue;
    }

    const el = parseElementLine(line);
    if (!el) continue;

    // Boundary event: varName contains a dot (host.localName)
    if (el.varName.includes('.')) {
      const dotIdx = el.varName.indexOf('.');
      const hostVar = el.varName.slice(0, dotIdx);
      const localName = el.varName.slice(dotIdx + 1);
      const host = elements.find((e) => e.varName === hostVar);
      if (host) {
        host.boundaryEvents.push({ localName, kind: el.kind, label: el.label });
      } else {
        pendingBoundary.push({ hostVar, localName, kind: el.kind, label: el.label });
      }
      continue;
    }

    elements.push({ varName: el.varName, kind: el.kind, label: el.label, boundaryEvents: [] });
  }

  // Resolve pending boundary events (declared before their host, unlikely but safe)
  for (const pb of pendingBoundary) {
    const host = elements.find((e) => e.varName === pb.hostVar);
    if (host) {
      host.boundaryEvents.push({ localName: pb.localName, kind: pb.kind, label: pb.label });
    }
  }

  return { elements, connections, associations };
};

/**
 * Main parser.
 * Returns null if input is not a valid BPMN DSL (doesn't start with `diagram "`).
 */
export const parseBpmnDsl = (src: string): DiagramAst | null => {
  if (!src || typeof src !== 'string') return null;

  const lines = cleanLines(src);
  if (!lines.length) return null;

  // Must have a diagram header
  const headerLine = lines.find((l) => /^diagram\s+"/.test(l));
  if (!headerLine) return null;

  const headerMatch = headerLine.match(/^diagram\s+"([^"]+)"/);
  if (!headerMatch) return null;

  const ast: DiagramAst = {
    name: headerMatch[1],
    lanes: [],
    globalElements: [],
    connections: [],
    associations: [],
    labels: {},
  };

  // Pending boundary events declared outside any lane (e.g., after the lane block)
  const pendingBoundary: Array<{ hostVar: string; localName: string; kind: BpmnKind | string; label?: string }> = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Lane block
    if (/^lane\s+"/.test(line) && line.includes('{')) {
      const laneMatch = line.match(/^lane\s+"([^"]+)"/);
      const laneName = laneMatch ? laneMatch[1] : `Lane_${ast.lanes.length + 1}`;
      // Parse optional <<systemCode>> annotation
      const sysCodeMatch = line.match(/<<([^>]+)>>/);
      const systemCode = sysCodeMatch ? sysCodeMatch[1].trim() : undefined;
      const { inner, nextIdx } = extractBlock(lines, i);
      const parsed = parseScopeLines(inner);
      ast.lanes.push({ name: laneName, elements: parsed.elements, systemCode });
      ast.connections.push(...parsed.connections);
      ast.associations.push(...parsed.associations);
      i = nextIdx;
      continue;
    }

    // Labels block
    if (/^labels\s*\{/.test(line) || (line === 'labels' && lines[i + 1]?.trim() === '{')) {
      const { inner, nextIdx } = extractBlock(lines, i);
      for (const labelLine of inner) {
        const eqIdx = labelLine.indexOf('=');
        if (eqIdx !== -1) {
          const key = labelLine.slice(0, eqIdx).trim();
          const val = labelLine
            .slice(eqIdx + 1)
            .trim()
            .replace(/^"|"$/g, '');
          ast.labels[key] = val;
        }
      }
      i = nextIdx;
      continue;
    }

    // Connection / association at top level
    const conn = parseConnections(line);
    if (conn.connections.length || conn.associations.length) {
      ast.connections.push(...conn.connections);
      ast.associations.push(...conn.associations);
      i++;
      continue;
    }

    // Element at top level (Annotation, Data.*, Group, etc.) or boundary event
    const el = parseElementLine(line);
    if (el) {
      if (el.varName.includes('.')) {
        const dotIdx = el.varName.indexOf('.');
        pendingBoundary.push({
          hostVar: el.varName.slice(0, dotIdx),
          localName: el.varName.slice(dotIdx + 1),
          kind: el.kind,
          label: el.label,
        });
      } else {
        ast.globalElements.push({ varName: el.varName, kind: el.kind, label: el.label, boundaryEvents: [] });
      }
    }

    i++;
  }

  // Resolve pending top-level boundary events
  const allLaneElements = ast.lanes.flatMap((l) => l.elements);
  for (const pb of pendingBoundary) {
    const host = [...allLaneElements, ...ast.globalElements].find((e) => e.varName === pb.hostVar);
    if (host) {
      host.boundaryEvents.push({ localName: pb.localName, kind: pb.kind, label: pb.label });
    }
  }

  // Apply labels block overrides
  const allElements = [...allLaneElements, ...ast.globalElements];
  for (const [varName, label] of Object.entries(ast.labels)) {
    const el = allElements.find((e) => e.varName === varName);
    if (el) el.label = label;
  }

  return ast;
};

// ─── XML Generator — Elements ─────────────────────────────────────────────────

const attr = (name: string, value: string | undefined) =>
  value ? ` ${name}="${value}"` : '';

/**
 * Generates BPMN XML for one element + its boundary events.
 * Returns a multi-line string (4-space indented).
 */
export const buildElementXml = (el: ElementDef): string => {
  const typeDef = (BPMN_TYPE_MAP as Record<string, BpmnTypeDef | undefined>)[el.kind];
  if (!typeDef) return `    <!-- Unknown type: ${el.kind} id="${el.varName}" -->`;

  const id = getBpmnId(el.varName, el.kind);
  const nameAttr = attr('name', el.label);
  const lines: string[] = [];

  if (typeDef.tag === 'bpmn:textAnnotation') {
    lines.push(`    <bpmn:textAnnotation id="${id}"${nameAttr}>`);
    lines.push(`      <bpmn:text>${el.label ?? ''}</bpmn:text>`);
    lines.push(`    </bpmn:textAnnotation>`);
  } else if (typeDef.eventDef) {
    const defId = `${id}_def`;
    lines.push(`    <${typeDef.tag} id="${id}"${nameAttr}>`);
    lines.push(`      <bpmn:${typeDef.eventDef} id="${defId}" />`);
    lines.push(`    </${typeDef.tag}>`);
  } else {
    lines.push(`    <${typeDef.tag} id="${id}"${nameAttr} />`);
  }

  // Boundary events
  for (const be of el.boundaryEvents) {
    const beDef = (BPMN_TYPE_MAP as Record<string, BpmnTypeDef | undefined>)[be.kind];
    if (!beDef) continue;
    const beId = getBpmnId(`${el.varName}.${be.localName}`, be.kind);
    const beLabel = attr('name', be.label);
    const cancelActivity = beDef.isNonInterrupting ? 'false' : 'true';
    lines.push(
      `    <bpmn:boundaryEvent id="${beId}"${beLabel} attachedToRef="${id}" cancelActivity="${cancelActivity}">`
    );
    if (beDef.eventDef) {
      lines.push(`      <bpmn:${beDef.eventDef} id="${beId}_def" />`);
    }
    lines.push(`    </bpmn:boundaryEvent>`);
  }

  return lines.join('\n');
};

// ─── Layout ───────────────────────────────────────────────────────────────────

const POOL_LABEL_W = 30;
const LANE_LABEL_W = 30;
const LANE_PAD_X = 30;
const LANE_PAD_Y = 20;
const ELEMENT_GAP_X = 60;
const POOL_ORIGIN_X = 100;
const POOL_ORIGIN_Y = 50;
const MIN_LANE_HEIGHT = 180;

interface Bounds { x: number; y: number; w: number; h: number }
interface Position { x: number; y: number; w: number; h: number }

export interface LayoutResult {
  /** varName → position (boundary events keyed as 'host.local') */
  positions: Record<string, Position>;
  poolBounds: Bounds;
  laneBounds: Array<{ laneId: string; bounds: Bounds }>;
}

export const buildColumnRanks = (
  lanes: LaneDef[],
  connections: ConnectionDef[]
): Record<string, number> => {
  const hostVar = (v: string) => (v.includes('.') ? v.slice(0, v.indexOf('.')) : v);
  const allVarNames = lanes.flatMap((l) => l.elements.map((e) => e.varName));

  const ranks: Record<string, number> = {};
  for (const v of allVarNames) ranks[v] = 0;

  const succs: Record<string, string[]> = {};
  for (const v of allVarNames) succs[v] = [];

  for (const conn of connections) {
    const from = hostVar(conn.from);
    const to = hostVar(conn.to);
    if (from === to) continue;
    if (from in succs && to in ranks) succs[from].push(to);
  }

  // Longest-path relaxation (up to N iterations)
  for (let iter = 0; iter < allVarNames.length; iter++) {
    let changed = false;
    for (const v of allVarNames) {
      for (const u of succs[v]) {
        if (ranks[u] < ranks[v] + 1) {
          ranks[u] = ranks[v] + 1;
          changed = true;
        }
      }
    }
    if (!changed) break;
  }

  // Detect cycles: if any edge can still be relaxed, the graph has a cycle.
  // In that case ranks are best-effort (Bellman-Ford partial result) and the
  // layout may not be strictly left-to-right for loopback connections.
  let hasCycle = false;
  for (const v of allVarNames) {
    for (const u of succs[v]) {
      if (ranks[u] < ranks[v] + 1) {
        hasCycle = true;
        break;
      }
    }
    if (hasCycle) break;
  }
  if (hasCycle) {
    // eslint-disable-next-line no-console
    console.warn('[bpmnDslParser] Cyclic connection detected; layout ranks are best-effort.');
  }

  // Enforce lane ordering: ranks must strictly increase within each lane
  for (const lane of lanes) {
    let prevRank = -1;
    for (const el of lane.elements) {
      if (ranks[el.varName] <= prevRank) {
        ranks[el.varName] = prevRank + 1;
        // Propagate via BFS
        const queue = [el.varName];
        while (queue.length > 0) {
          const v = queue.shift()!;
          for (const u of succs[v]) {
            if (ranks[u] < ranks[v] + 1) {
              ranks[u] = ranks[v] + 1;
              queue.push(u);
            }
          }
        }
      }
      prevRank = ranks[el.varName];
    }
  }

  return ranks;
};

export const calculateLayout = (
  lanes: LaneDef[],
  globalElements: ElementDef[],
  connections: ConnectionDef[] = []
): LayoutResult => {
  const positions: Record<string, Position> = {};
  const laneBoundsArr: Array<{ laneId: string; bounds: Bounds }> = [];

  const colRanks = buildColumnRanks(lanes, connections);
  const allLaneEls = lanes.flatMap((l) => l.elements);
  const maxRank = allLaneEls.length > 0 ? Math.max(...allLaneEls.map((e) => colRanks[e.varName])) : 0;

  // Compute max element width per column rank
  const colWidths: number[] = new Array(maxRank + 1).fill(0);
  for (const lane of lanes) {
    for (const el of lane.elements) {
      const def = (BPMN_TYPE_MAP as Record<string, BpmnTypeDef | undefined>)[el.kind] ?? { w: 100, h: 80 };
      const r = colRanks[el.varName];
      colWidths[r] = Math.max(colWidths[r], def.w);
    }
  }

  // Compute x position for each column rank
  const startX = POOL_ORIGIN_X + POOL_LABEL_W + LANE_LABEL_W + LANE_PAD_X;
  const colX: number[] = [];
  let accX = startX;
  for (let r = 0; r <= maxRank; r++) {
    colX[r] = accX;
    accX += colWidths[r] + ELEMENT_GAP_X;
  }

  const maxLaneContentWidth = Math.max(
    accX - POOL_ORIGIN_X - POOL_LABEL_W - LANE_LABEL_W,
    300
  );
  const poolWidth = POOL_LABEL_W + LANE_LABEL_W + maxLaneContentWidth;

  let currentY = POOL_ORIGIN_Y;

  for (let li = 0; li < lanes.length; li++) {
    const lane = lanes[li];
    let maxElemH = MIN_LANE_HEIGHT;
    for (const el of lane.elements) {
      const def = (BPMN_TYPE_MAP as Record<string, BpmnTypeDef | undefined>)[el.kind] ?? { w: 100, h: 80 };
      maxElemH = Math.max(maxElemH, def.h + LANE_PAD_Y * 2);
    }
    // Extra height if any element has boundary events
    const hasBoundary = lane.elements.some((e) => e.boundaryEvents.length > 0);
    if (hasBoundary) maxElemH += 36;

    const laneHeight = maxElemH;
    const laneId = `Lane_${li + 1}`;

    laneBoundsArr.push({
      laneId,
      bounds: {
        x: POOL_ORIGIN_X + POOL_LABEL_W,
        y: currentY,
        w: poolWidth - POOL_LABEL_W,
        h: laneHeight,
      },
    });

    // Position elements within lane using column ranks
    for (const el of lane.elements) {
      const def = (BPMN_TYPE_MAP as Record<string, BpmnTypeDef | undefined>)[el.kind] ?? { w: 100, h: 80 };
      const elY = currentY + Math.round((laneHeight - def.h) / 2);
      const curX = colX[colRanks[el.varName]];
      positions[el.varName] = { x: curX, y: elY, w: def.w, h: def.h };

      // Distribute boundary events evenly along the bottom edge of the host
      const nBE = el.boundaryEvents.length;
      if (nBE > 0) {
        const beDefs = el.boundaryEvents.map(
          (be) => (BPMN_TYPE_MAP as Record<string, BpmnTypeDef | undefined>)[be.kind] ?? { w: 36, h: 36 }
        );
        const BE_GAP = 8;
        const totalBeW = beDefs.reduce((s, d) => s + d.w, 0) + (nBE - 1) * BE_GAP;
        let beX = curX + Math.max(0, Math.round((def.w - totalBeW) / 2));
        el.boundaryEvents.forEach((be, beIdx) => {
          const beKey = `${el.varName}.${be.localName}`;
          const beDef = beDefs[beIdx];
          positions[beKey] = {
            x: beX,
            y: elY + def.h - Math.round(beDef.h / 2),
            w: beDef.w,
            h: beDef.h,
          };
          beX += beDef.w + BE_GAP;
        });
      }
    }

    currentY += laneHeight;
  }

  // Position global elements (Annotation, Data.*, Group, etc.) below all lanes
  let globalX = POOL_ORIGIN_X + POOL_LABEL_W + LANE_LABEL_W + LANE_PAD_X;
  const globalY = currentY + 40;
  for (const el of globalElements) {
    const def = (BPMN_TYPE_MAP as Record<string, BpmnTypeDef | undefined>)[el.kind] ?? { w: 100, h: 40 };
    positions[el.varName] = { x: globalX, y: globalY, w: def.w, h: def.h };
    globalX += def.w + ELEMENT_GAP_X;
  }

  const totalHeight = currentY - POOL_ORIGIN_Y + (globalElements.length > 0 ? 100 : 0);

  return {
    positions,
    poolBounds: { x: POOL_ORIGIN_X, y: POOL_ORIGIN_Y, w: poolWidth, h: totalHeight },
    laneBounds: laneBoundsArr,
  };
};

// ─── XML Generator — Full Document ────────────────────────────────────────────

const toProcessId = (name: string): string =>
  'Process_' + name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');

const buildShape = (id: string, pos: Position, extra = ''): string =>
  [
    `      <bpmndi:BPMNShape id="${id}_di" bpmnElement="${id}"${extra}>`,
    `        <dc:Bounds x="${pos.x}" y="${pos.y}" width="${pos.w}" height="${pos.h}" />`,
    `      </bpmndi:BPMNShape>`,
  ].join('\n');

/**
 * Generates complete BPMN 2.0 XML from a parsed DiagramAst.
 * Returns null if there are no elements to render.
 */
export const generateBpmnXmlFromDsl = (ast: DiagramAst): string | null => {
  if (!ast) return null;
  const allElements = [...ast.lanes.flatMap((l) => l.elements), ...ast.globalElements];
  if (!allElements.length) return null;

  const processId = toProcessId(ast.name);
  const layout = calculateLayout(ast.lanes, ast.globalElements, ast.connections);

  // ── laneSet ──────────────────────────────────────────────────────────────
  const laneXml: string[] = [];
  if (ast.lanes.length > 0) {
    laneXml.push(`    <bpmn:laneSet id="LaneSet_1">`);
    ast.lanes.forEach((lane, idx) => {
      const laneId = `Lane_${idx + 1}`;
      const sysAttr = lane.systemCode ? ` udp:systemCode="${lane.systemCode}"` : '';
      laneXml.push(`      <bpmn:lane id="${laneId}" name="${lane.name}"${sysAttr}>`);
      for (const el of lane.elements) {
        const elId = getBpmnId(el.varName, el.kind);
        laneXml.push(`        <bpmn:flowNodeRef>${elId}</bpmn:flowNodeRef>`);
      }
      laneXml.push(`      </bpmn:lane>`);
    });
    laneXml.push(`    </bpmn:laneSet>`);
  }

  // ── process elements ─────────────────────────────────────────────────────
  const processEls = allElements.map((el) => buildElementXml(el));

  // ── sequence flows ────────────────────────────────────────────────────────
  const resolveId = (varName: string): string => {
    if (varName.includes('.')) {
      const dotIdx = varName.indexOf('.');
      const hostVar = varName.slice(0, dotIdx);
      const localName = varName.slice(dotIdx + 1);
      const host = allElements.find((e) => e.varName === hostVar);
      const be = host?.boundaryEvents.find((b) => b.localName === localName);
      if (be) return getBpmnId(varName, be.kind);
      return `BoundaryEvent_${varName.replace(/\./g, '_')}`;
    }
    const el = allElements.find((e) => e.varName === varName);
    return el ? getBpmnId(el.varName, el.kind) : varName;
  };

  const flowXml = ast.connections.map((conn, idx) => {
    const fromId = resolveId(conn.from);
    const toId = resolveId(conn.to);
    const nameAttr = conn.label ? ` name="${conn.label}"` : '';
    return `    <bpmn:sequenceFlow id="Flow_${idx + 1}"${nameAttr} sourceRef="${fromId}" targetRef="${toId}" />`;
  });

  // ── associations ──────────────────────────────────────────────────────────
  const assocXml = ast.associations.map((a, idx) => {
    const fromId = resolveId(a.from);
    const toId = resolveId(a.to);
    return `    <bpmn:association id="Association_${idx + 1}" sourceRef="${fromId}" targetRef="${toId}" />`;
  });

  // ── BPMNShapes ────────────────────────────────────────────────────────────
  const shapes: string[] = [];

  // Pool shape
  const pb = layout.poolBounds;
  shapes.push([
    `      <bpmndi:BPMNShape id="${processId}_di" bpmnElement="${processId}" isHorizontal="true">`,
    `        <dc:Bounds x="${pb.x}" y="${pb.y}" width="${pb.w}" height="${pb.h}" />`,
    `      </bpmndi:BPMNShape>`,
  ].join('\n'));

  // Lane shapes
  layout.laneBounds.forEach(({ laneId, bounds }) => {
    shapes.push([
      `      <bpmndi:BPMNShape id="${laneId}_di" bpmnElement="${laneId}" isHorizontal="true">`,
      `        <dc:Bounds x="${bounds.x}" y="${bounds.y}" width="${bounds.w}" height="${bounds.h}" />`,
      `      </bpmndi:BPMNShape>`,
    ].join('\n'));
  });

  // Element shapes
  for (const el of allElements) {
    const elId = getBpmnId(el.varName, el.kind);
    const pos = layout.positions[el.varName];
    if (pos) shapes.push(buildShape(elId, pos));

    // Boundary event shapes
    for (const be of el.boundaryEvents) {
      const beKey = `${el.varName}.${be.localName}`;
      const beId = getBpmnId(beKey, be.kind);
      const bePos = layout.positions[beKey];
      if (bePos) shapes.push(buildShape(beId, bePos));
    }
  }

  // ── BPMNEdges — with smart same-lane skip routing ─────────────────────────
  const edges: string[] = [];

  // Build varName → lane index map (includes boundary event keys like 'task.timer')
  const elementLaneIdx: Record<string, number> = {};
  ast.lanes.forEach((lane, laneIdx) => {
    for (const el of lane.elements) {
      elementLaneIdx[el.varName] = laneIdx;
      for (const be of el.boundaryEvents) {
        elementLaneIdx[`${el.varName}.${be.localName}`] = laneIdx;
      }
    }
  });

  // For a boundary event source (e.g. 'validate.timer'), get its host varName
  const hostOf = (v: string) => (v.includes('.') ? v.slice(0, v.indexOf('.')) : null);

  ast.connections.forEach((conn, idx) => {
    const fromPos = layout.positions[conn.from];
    const toPos   = layout.positions[conn.to];
    if (!fromPos || !toPos) return;

    const x1 = fromPos.x + fromPos.w;
    const y1 = fromPos.y + Math.round(fromPos.h / 2);
    const x2 = toPos.x;
    const y2 = toPos.y + Math.round(toPos.h / 2);

    let waypoints: Array<{ x: number; y: number }>;

    const fromLane = elementLaneIdx[conn.from] ?? -1;
    const toLane   = elementLaneIdx[conn.to]   ?? -1;

    if (fromLane >= 0 && fromLane === toLane && x1 < x2) {
      // Same lane, forward flow — detect elements sitting between source and target
      const laneEls = ast.lanes[fromLane].elements;
      const srcHost = hostOf(conn.from);
      const blocking = laneEls.filter((el) => {
        const pos = layout.positions[el.varName];
        if (!pos) return false;
        if (el.varName === conn.from || el.varName === conn.to) return false;
        if (srcHost && el.varName === srcHost) return false; // skip source's host element
        return pos.x + pos.w > x1 && pos.x < x2;
      });

      if (blocking.length > 0) {
        // Route with a U-bend below all elements (and boundary events) in this lane
        let maxBottom = 0;
        for (const el of laneEls) {
          const pos = layout.positions[el.varName];
          if (pos) maxBottom = Math.max(maxBottom, pos.y + pos.h);
          for (const be of el.boundaryEvents) {
            const bePos = layout.positions[`${el.varName}.${be.localName}`];
            if (bePos) maxBottom = Math.max(maxBottom, bePos.y + bePos.h);
          }
        }
        const routeY = maxBottom + 20;
        waypoints = [
          { x: x1, y: y1 },
          { x: x1, y: routeY },
          { x: x2, y: routeY },
          { x: x2, y: y2 },
        ];
      } else {
        waypoints = [{ x: x1, y: y1 }, { x: x2, y: y2 }];
      }
    } else {
      waypoints = [{ x: x1, y: y1 }, { x: x2, y: y2 }];
    }

    const waypointXml = waypoints
      .map((wp) => `        <di:waypoint x="${wp.x}" y="${wp.y}" />`)
      .join('\n');

    edges.push([
      `      <bpmndi:BPMNEdge id="Flow_${idx + 1}_di" bpmnElement="Flow_${idx + 1}">`,
      waypointXml,
      `      </bpmndi:BPMNEdge>`,
    ].join('\n'));
  });

  // ── Assemble ──────────────────────────────────────────────────────────────
  return [
    `<?xml version="1.0" encoding="UTF-8"?>`,
    `<bpmn:definitions`,
    `  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"`,
    `  xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL"`,
    `  xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"`,
    `  xmlns:dc="http://www.omg.org/spec/DD/20100524/DC"`,
    `  xmlns:di="http://www.omg.org/spec/DD/20100524/DI"`,
    `  xmlns:udp="http://udp-frontend/schema/extensions"`,
    `  id="Definitions_1"`,
    `  targetNamespace="http://bpmn.io/schema/bpmn">`,
    `  <bpmn:process id="${processId}" name="${ast.name}" isExecutable="false">`,
    ...laneXml,
    ...processEls,
    ...flowXml,
    ...assocXml,
    `  </bpmn:process>`,
    `  <bpmndi:BPMNDiagram id="BPMNDiagram_1">`,
    `    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="${processId}">`,
    ...shapes,
    ...edges,
    `    </bpmndi:BPMNPlane>`,
    `  </bpmndi:BPMNDiagram>`,
    `</bpmn:definitions>`,
  ].join('\n');
};

// ─── DSL Detector ─────────────────────────────────────────────────────────────

/** Returns true if the source is a BPMN DSL diagram (starts with `diagram "`). */
export const isBpmnDsl = (src: string): boolean => {
  if (!src || typeof src !== 'string') return false;
  return /^\s*diagram\s+"/m.test(src);
};

// ─── Default Example ──────────────────────────────────────────────────────────

export const DEFAULT_BPMN_DSL = `// BPMN DSL — simple syntax, no boilerplate needed

diagram "Newsletter Subscription"

lane "User" {
  start   = Start
  submit  = Task.User "Fill Registration Form"
  confirm = Task.User "Confirm Email"
  end_ok  = End
}

lane "Backend" {
  validate  = Task.Service "Validate Email"
  validate.timer = Boundary.Timer "30s"
  validate.err   = Boundary.Error

  gw        = Gateway.Exclusive "Is email valid?"
  send_mail = Task.Send "Send Confirmation"
  end_err   = End.Error
}

// Connections
start -> submit
submit -> validate: Form submitted
validate -> gw
gw -> send_mail: Valid
gw -> end_err: Invalid
validate.timer -> end_err: Timed out
send_mail -> confirm
confirm -> end_ok

// Annotation
note1 = Annotation "Email must be RFC 5321 compliant"
validate --> note1
`;

// ─── Reverse Type Maps ────────────────────────────────────────────────────────

/** tag (or tag:eventDefLocalName) → BpmnKind (prefers more-specific dot-notation kinds) */
const REVERSE_TYPE_MAP: Record<string, BpmnKind | string> = (() => {
  const m: Record<string, BpmnKind | string> = {};
  for (const [kind, def] of Object.entries(BPMN_TYPE_MAP) as [string, BpmnTypeDef][]) {
    if (def.tag === 'bpmn:boundaryEvent') continue;
    const key = def.eventDef ? `${def.tag}:${def.eventDef}` : def.tag;
    // Prefer dot-notation kinds (e.g. 'Gateway.Exclusive' over 'Gateway')
    if (!(key in m) || (kind as string).includes('.')) m[key] = kind;
  }
  return m;
})();

/** tag:eventDefLocalName[:ni] → BpmnKind (for boundary events) */
const REVERSE_BOUNDARY_MAP: Record<string, BpmnKind | string> = (() => {
  const m: Record<string, BpmnKind | string> = {};
  for (const [kind, def] of Object.entries(BPMN_TYPE_MAP) as [string, BpmnTypeDef][]) {
    if (def.tag !== 'bpmn:boundaryEvent') continue;
    const base = def.eventDef ? `${def.tag}:${def.eventDef}` : def.tag;
    const key = def.isNonInterrupting ? `${base}:ni` : base;
    m[key] = kind;
  }
  return m;
})();

/** Convert a BPMN element ID to a valid DSL variable name */
const idToVarName = (bpmnId: string): string =>
  bpmnId.replace(/[^a-zA-Z0-9]/g, '_').replace(/^_+/, '').toLowerCase();

// ─── xmlToDsl ──────────────────────────────────────────────────────────────

/**
 * Reconstructs a BPMN DSL string from a BPMN 2.0 XML string.
 *
 * @param xml        - Full BPMN XML (from modelerRef.current.saveXML())
 * @param elementMap - Optional map built from the last parseBpmnDsl call
 *                     (preserves user variable names for known elements).
 * @returns DSL string, or null if XML cannot be parsed.
 */
export const xmlToDsl = (
  xml: string,
  elementMap: DslElementMap = new Map()
): string | null => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, 'application/xml');
  if (doc.querySelector('parsererror')) return null;

  const ns = (tag: string) => [tag, tag.replace('bpmn:', '')];
  const qs = (root: Element | Document, ...tags: string[]) => {
    for (const tag of tags) {
      const el = root.querySelector(tag);
      if (el) return el;
    }
    return null;
  };
  const qsAll = (root: Element | Document, ...tags: string[]): Element[] => {
    for (const tag of tags) {
      const els = Array.from(root.querySelectorAll(tag));
      if (els.length) return els as Element[];
    }
    return [];
  };

  const process = qs(doc, 'bpmn\\:process', 'process');
  if (!process) return null;

  const diagramName = process.getAttribute('name') || 'Untitled';

  // Collect all direct children of process by ID
  const elementById = new Map<string, Element>();
  for (const el of Array.from(process.children)) {
    const id = el.getAttribute('id');
    if (id) elementById.set(id, el);
  }

  // Lane info
  const laneNodes = qsAll(doc, 'bpmn\\:lane', 'lane');
  const laneNames = new Map<string, string>();
  const laneSystemCodes = new Map<string, string>();
  const laneFlowNodeIds = new Map<string, string[]>();
  const elementToLaneId = new Map<string, string>();

  for (const laneEl of laneNodes) {
    const laneId = laneEl.getAttribute('id') ?? '';
    laneNames.set(laneId, laneEl.getAttribute('name') ?? laneId);
    const sc = laneEl.getAttribute('udp:systemCode') ?? laneEl.getAttribute('systemCode') ?? '';
    if (sc) laneSystemCodes.set(laneId, sc);
    const refs = qsAll(laneEl, 'bpmn\\:flowNodeRef', 'flowNodeRef')
      .map(r => r.textContent?.trim() ?? '').filter(Boolean);
    laneFlowNodeIds.set(laneId, refs);
    for (const ref of refs) elementToLaneId.set(ref, laneId);
  }

  // Boundary events: attachedToRef → [element]
  const boundaryByHost = new Map<string, Element[]>();
  for (const [, el] of elementById) {
    if (!ns('bpmn:boundaryEvent').includes(el.tagName)) continue;
    const host = el.getAttribute('attachedToRef') ?? '';
    if (!boundaryByHost.has(host)) boundaryByHost.set(host, []);
    boundaryByHost.get(host)!.push(el);
  }

  // Helpers
  const getKind = (el: Element): BpmnKind | string => {
    const id = el.getAttribute('id') ?? '';
    if (elementMap.has(id)) return elementMap.get(id)!.kind;
    const tag = el.tagName;
    const eventDefEl = Array.from(el.children).find(c =>
      c.localName?.endsWith('EventDefinition')
    );
    const evtLocal = eventDefEl?.localName ?? '';
    if (ns('bpmn:boundaryEvent').includes(tag)) {
      const isNI = el.getAttribute('cancelActivity') === 'false';
      const base = evtLocal ? `${tag}:${evtLocal}` : tag;
      return REVERSE_BOUNDARY_MAP[isNI ? `${base}:ni` : base] ?? 'Boundary.Timer';
    }
    const key = evtLocal ? `${tag}:${evtLocal}` : tag;
    return REVERSE_TYPE_MAP[key] ?? 'Task';
  };

  const getVar = (id: string): string =>
    elementMap.has(id) ? elementMap.get(id)!.varName : idToVarName(id);

  const getLabel = (el: Element, id: string): string | undefined => {
    if (elementMap.has(id)) return elementMap.get(id)!.label;
    return el.getAttribute('name') || undefined;
  };

  const fmtElement = (el: Element, indent: string): string => {
    const id = el.getAttribute('id') ?? '';
    const kind = getKind(el);
    const varName = getVar(id);
    const label = getLabel(el, id);
    const labelPart = label ? ` "${label}"` : '';
    const lines = [`${indent}${varName} = ${kind}${labelPart}`];
    // Boundary events attached to this element
    for (const be of boundaryByHost.get(id) ?? []) {
      const beId = be.getAttribute('id') ?? '';
      const beKind = getKind(be);
      const beVarFull = getVar(beId);
      const hostVar = getVar(id);
      // beVarFull may be 'host.local' (from map) or just an id-derived name
      let beLocal: string;
      if (beVarFull.includes('.') && beVarFull.startsWith(hostVar + '.')) {
        beLocal = beVarFull.slice(hostVar.length + 1);
      } else {
        beLocal = beVarFull.replace(/[^a-zA-Z0-9_]/g, '_');
      }
      const beLabel = getLabel(be, beId);
      const beLabelPart = beLabel ? ` "${beLabel}"` : '';
      lines.push(`${indent}${hostVar}.${beLocal} = ${beKind}${beLabelPart}`);
    }
    return lines.join('\n');
  };

  const SKIP_TAGS = new Set([
    'bpmn:laneSet', 'bpmn:sequenceFlow', 'bpmn:association',
    'bpmn:boundaryEvent', 'bpmn:dataObject',
    // non-namespaced variants
    'laneSet', 'sequenceFlow', 'association', 'boundaryEvent', 'dataObject',
  ]);
  const GLOBAL_TAGS = new Set([
    'bpmn:textAnnotation', 'bpmn:dataObjectReference',
    'bpmn:dataStoreReference', 'bpmn:group',
    'textAnnotation', 'dataObjectReference', 'dataStoreReference', 'group',
  ]);

  const output: string[] = [`diagram "${diagramName}"`, ''];

  // ── Lanes ────────────────────────────────────────────────────────────────
  if (laneNodes.length > 0) {
    for (const laneEl of laneNodes) {
      const laneId = laneEl.getAttribute('id') ?? '';
      const laneName = laneNames.get(laneId) ?? laneId;
      const sc = laneSystemCodes.get(laneId);
      const scPart = sc ? ` <<${sc}>>` : '';
      output.push(`lane "${laneName}"${scPart} {`);
      for (const ref of laneFlowNodeIds.get(laneId) ?? []) {
        const el = elementById.get(ref);
        if (!el) continue;
        if (SKIP_TAGS.has(el.tagName)) continue;
        if (ns('bpmn:boundaryEvent').includes(el.tagName)) continue;
        output.push(fmtElement(el, '  '));
      }
      output.push('}', '');
    }
  } else {
    // No lanes: single default lane
    output.push('lane "Main" {');
    for (const [, el] of elementById) {
      if (SKIP_TAGS.has(el.tagName)) continue;
      if (ns('bpmn:boundaryEvent').includes(el.tagName)) continue;
      if (GLOBAL_TAGS.has(el.tagName)) continue;
      output.push(fmtElement(el, '  '));
    }
    output.push('}', '');
  }

  // ── Global elements (Annotation, Data.*, Group outside lanes) ────────────
  const inLane = new Set(elementToLaneId.keys());
  const globalLines: string[] = [];
  for (const [id, el] of elementById) {
    if (SKIP_TAGS.has(el.tagName)) continue;
    if (ns('bpmn:boundaryEvent').includes(el.tagName)) continue;
    if (inLane.has(id)) continue;
    if (!GLOBAL_TAGS.has(el.tagName)) continue;
    globalLines.push(fmtElement(el, ''));
  }
  if (globalLines.length) output.push(...globalLines, '');

  // ── Sequence flows ────────────────────────────────────────────────────────
  const flowLines: string[] = [];
  for (const [, el] of elementById) {
    if (!ns('bpmn:sequenceFlow').includes(el.tagName)) continue;
    const src = el.getAttribute('sourceRef') ?? '';
    const tgt = el.getAttribute('targetRef') ?? '';
    if (!src || !tgt) continue;
    const label = el.getAttribute('name');
    const labelPart = label ? `: ${label}` : '';
    flowLines.push(`${getVar(src)} -> ${getVar(tgt)}${labelPart}`);
  }
  if (flowLines.length) output.push(...flowLines, '');

  // ── Associations ──────────────────────────────────────────────────────────
  const assocLines: string[] = [];
  for (const [, el] of elementById) {
    if (!ns('bpmn:association').includes(el.tagName)) continue;
    const src = el.getAttribute('sourceRef') ?? '';
    const tgt = el.getAttribute('targetRef') ?? '';
    if (!src || !tgt) continue;
    assocLines.push(`${getVar(src)} --> ${getVar(tgt)}`);
  }
  if (assocLines.length) output.push(...assocLines);

  return output.join('\n').replace(/\n{3,}/g, '\n\n').trim() + '\n';
};
